import AzureAuthenticationRedirectContext from "src/azure/azure-authentication-redirect-context";

// VMD-405: MS 리다이렉트 복귀 재시도.
//
// VMD-399 는 이 재시도를 렌더 본문에서 useEffect 로 옮기면서, 그 안에서
// AzureAuthenticationButton 을 함수로 직접 호출했다. 그 컴포넌트는 본문에서
// useState 를 부르는데 useEffect 콜백은 렌더 단계가 아니라 React 가 디스패처를
// ContextOnlyDispatcher 로 되돌린 뒤이므로, 호출이 Invalid hook call 로 던지고
// 곁의 try/catch 가 그것을 console.log 로 삼켰다. 그래서 재시도가 한 번도
// 발사되지 않았고 사용자는 Microsoft 버튼을 두 번 더 눌러야 했다.
//
// 그러므로 컴포넌트를 거치지 않는다. 리다이렉트 컨텍스트를 직접 만들어 쓰면
// 훅이 렌더 밖에서 불릴 일이 없다.

export interface MsRedirectLogin {
  login: (onToken: (token: string) => void) => Promise<void>;
}

// MS 리다이렉트 복귀는 해시에 code 파라미터로 돌아온다. 문자열 포함 검사로 읽으면
// #decode 같은 해시를 참으로 보고, 반대로 파라미터 순서가 바뀌어 #state=...&code=...
// 로 오면 거짓으로 본다. 거짓으로 보는 쪽이 바로 이 결함의 모양이므로 파라미터로 읽는다.
export const shouldRetryMsLogin = (hash: string): boolean =>
  new URLSearchParams(hash.replace(/^#/, "")).has("code");

// 오류 객체를 통째로 남기지 않는다. MSAL 오류에는 account 및 idTokenClaims 같은
// 중첩 객체가 달려 있어 운영 콘솔에 토큰 조각이 남을 수 있다. 진단에 필요한 식별자만 뽑는다.
const describeError = (error: unknown): string => {
  if (error && typeof error === "object") {
    const e = error as { errorCode?: string; name?: string; message?: string };
    const parts = [e.errorCode, e.name, e.message].filter(Boolean);
    if (parts.length > 0) return parts.join(" / ");
  }
  return String(error);
};

// 로그 메시지를 조각이 아니라 완성된 문자열로 받는다. 조각을 받아 템플릿으로
// 합치면 재시도 쪽 메시지가 한 글자라도 바뀌고, 그 문자열은 테스트가 그대로
// 비교하고 있어 조용한 회귀가 된다 (실제로 2026-10-27 에 그렇게 깨뜨렸다).
const startRedirectLogin = async (
  onToken: (token: string) => void,
  createContext: () => MsRedirectLogin,
  logMessage: string,
): Promise<boolean> => {
  try {
    await createContext().login(onToken);
    return true;
  } catch (error) {
    // 조용히 삼키지 않는다 — 이 결함이 13일 동안 보이지 않은 자리가 여기다.
    console.error(logMessage, describeError(error));
    return false;
  }
};

export const runMsLoginRetry = async (
  onToken: (token: string) => void,
  createContext: () => MsRedirectLogin = () => new AzureAuthenticationRedirectContext(),
): Promise<boolean> => startRedirectLogin(onToken, createContext, "[VMD-405] MS 로그인 재시도 실패");

// 버튼을 눌러 시작하는 평상시 로그인. 복귀 재시도와 같은 직접 경로를 쓰지만 로그
// 메시지를 나눠 두어야 운영 콘솔에서 복귀 실패와 첫 클릭 실패를 구분할 수 있다.
export const startMsLogin = async (
  onToken: (token: string) => void,
  createContext: () => MsRedirectLogin = () => new AzureAuthenticationRedirectContext(),
): Promise<boolean> => startRedirectLogin(onToken, createContext, "[MS 로그인] 버튼 로그인 실패");
